import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, MessageSquare, Users, Clock, CheckCircle, 
  Search, TrendingUp, TrendingDown, LogOut, Settings,
  ShoppingBag, DollarSign, Activity, Shield, XCircle,
  Phone, Mail, Calendar, Package, Eye, Edit2, RefreshCw, FileText, Server
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContextMySQL';
import { authHeaders, API_URL } from '../api';
import AdminArticles from './AdminArticles';
import AdminServers from './AdminServers';
import CoinLoader from '../components/CoinLoader';
import io from 'socket.io-client';

const SOCKET_URL = API_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, logout, loading } = useAuth();
  const [socket, setSocket] = useState(null);
  
  // Dashboard data
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [statistics, setStatistics] = useState({});
  
  // UI state
  const [activeTab, setActiveTab] = useState('orders'); // 'orders', 'users', 'conversations'
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'active', 'completed', 'cancelled'
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [usingLocalStorage, setUsingLocalStorage] = useState(false);

  useEffect(() => {
    // Wait for auth to load before checking
    if (loading) return;
    
    // Check if user is admin
    if (!isAuthenticated || !isAdmin) {
      navigate('/');
      return;
    }

    // Fetch dashboard data
    fetchDashboardData();
    
    // Setup Socket.io connection (jeton de session signé)
    const newSocket = io(SOCKET_URL, {
      auth: { token: user?.token }
    });
    
    newSocket.on('connect', () => {
      console.log('Admin connected to Socket.io');
    });
    
    newSocket.on('new_message', (message) => {
      console.log('New message received:', message);
      fetchDashboardData(); // Refresh data on new messages
    });
    
    newSocket.on('order_status_updated', (data) => {
      console.log('Order status updated:', data);
      fetchDashboardData(); // Refresh data on order updates
    });
    
    newSocket.on('new_order', (data) => {
      console.log('New order received:', data);
      fetchDashboardData(); // Refresh when a new order arrives
    });
    
    setSocket(newSocket);
    
    return () => {
      newSocket.disconnect();
    };
  }, [isAuthenticated, isAdmin, loading, navigate, user]);

  // Poll localStorage for new orders/chats every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData(true); // silent - no loading spinner
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async (silent = false) => {
    if (!silent) setLoadingData(true);
    const localOrders = JSON.parse(localStorage.getItem('admin_orders') || '[]');
    const chatHistory = JSON.parse(localStorage.getItem('chatHistory') || '[]');
    
    // Also derive orders from chatHistory (for orders created before admin_orders existed)
    const localOrderIds = new Set(localOrders.map(o => o.id));
    chatHistory.forEach(chat => {
      const od = chat.orderData;
      if (od?.id && !localOrderIds.has(od.id)) {
        localOrders.push({
          id: od.id,
          offer_id: null,
          type: od.type,
          status: od.status || 'pending',
          server: od.server,
          category: od.category,
          amount: od.quantity || od.amount,
          price: od.pricePerM || od.price,
          total_price: od.totalPrice || od.total_price,
          payment_method: od.paymentMethod || od.payment_method || od.bankName || 'N/A',
          terms: od.terms || '',
          advertiser_id: 1,
          advertiser_name: 'AVKAMAS Site',
          trader_id: od.userId || 1,
          trader_name: od.email,
          email: od.email,
          phone: od.phone,
          character: od.character,
          created_at: chat.createdAt || od.timestamp
        });
        localOrderIds.add(od.id);
      }
    });
    
    // Build local users from chat history
    const uniqueUsers = new Set();
    chatHistory.forEach(chat => {
      if (chat.orderData?.email) uniqueUsers.add(chat.orderData.email);
    });
    const localUsers = Array.from(uniqueUsers).map(email => ({
      id: Math.random().toString(),
      name: email.split('@')[0],
      email: email,
      role: 'user',
      created_at: new Date().toISOString()
    }));
    
    // Build local conversations from chat history
    const localConversations = chatHistory.map(chat => ({
      id: chat.id,
      type: 'support',
      status: 'active',
      title: `Support Commande #${chat.orderData?.id?.slice(-8) || 'N/A'}`,
      order_id: chat.orderData?.id,
      created_at: chat.createdAt,
      updated_at: chat.createdAt,
      user_name: chat.orderData?.email?.split('@')[0] || 'Unknown',
      orderData: chat.orderData,
      messages: chat.messages
    }));

    // Fetch orders from Node server (port 3001) - shared across all browsers
    let nodeOrders = [];
    try {
      const nodeRes = await fetch(`${SOCKET_URL}/api/admin/orders`, { headers: authHeaders() });
      const nodeData = await nodeRes.json();
      if (nodeData.success) nodeOrders = nodeData.orders || [];
    } catch (e) {
      // Node server unavailable
    }

    // Merge Node server orders + localStorage (PHP API is not served by Apache)
    const seenIds = new Set();
    const mergedOrders = [...nodeOrders, ...localOrders].filter(o => {
      if (seenIds.has(o.id)) return false;
      seenIds.add(o.id);
      return true;
    });
    setUsingLocalStorage(nodeOrders.length === 0);
    setOrders(mergedOrders);
    setUsers(localUsers);
    setConversations(localConversations);
    setStatistics({
      total_orders: mergedOrders.length,
      total_users: localUsers.length,
      total_conversations: localConversations.length,
      active_orders: mergedOrders.filter(o => o.status === 'active' || o.status === 'pending').length,
      completed_orders: mergedOrders.filter(o => o.status === 'completed').length,
      pending_orders: mergedOrders.filter(o => o.status === 'pending').length
    });
    setLoadingData(false);
  };

  const handleOrderStatusUpdate = async (orderId, newStatus) => {
    // Always update localStorage first (works for both admin_orders and chatHistory-derived orders)
    const adminOrders = JSON.parse(localStorage.getItem('admin_orders') || '[]');
    const updatedAdminOrders = adminOrders.map(order => 
      order.id === orderId ? { ...order, status: newStatus } : order
    );
    localStorage.setItem('admin_orders', JSON.stringify(updatedAdminOrders));

    // Shared id so the localStorage copy and server copy dedupe in chats
    const statusClientId = `status-${orderId}-${newStatus}-${Date.now()}`;
    const statusText = newStatus === 'active' ? '✅ Commande approuvée par l\'admin'
      : newStatus === 'cancelled' ? '❌ Commande refusée par l\'admin'
      : newStatus === 'completed' ? '🎉 Commande complétée!'
      : `Statut mis à jour: ${newStatus}`;

    // Also update orderData.status inside chatHistory so it stays consistent
    const chatHistory = JSON.parse(localStorage.getItem('chatHistory') || '[]');
    const updatedHistory = chatHistory.map(c => {
      if (c.orderData?.id === orderId || c.orderData?.id?.toString() === orderId) {
        return {
          ...c,
          orderData: { ...c.orderData, status: newStatus },
          messages: [...(c.messages || []), {
            id: statusClientId,
            sender: 'system',
            text: statusText,
            timestamp: new Date().toISOString(),
            status: 'read'
          }]
        };
      }
      return c;
    });
    localStorage.setItem('chatHistory', JSON.stringify(updatedHistory));

    // Optimistically update UI
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));

    // Persist to Node server (shared across all browsers)
    try {
      await fetch(`${SOCKET_URL}/api/admin/orders/update_status`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ orderId, status: newStatus })
      });
      // Also post a system message to the order's support chat
      fetch(`${SOCKET_URL}/api/support_messages`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          orderId,
          sender: 'system',
          text: statusText,
          clientId: statusClientId,
          clientTs: new Date().toISOString()
        })
      }).catch(() => {});
    } catch (error) {
      // Node server unavailable - localStorage update already done
    }

    fetchDashboardData(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    // Users are derived from chatHistory emails - remove their chats and orders locally
    const targetUser = users.find(u => u.id === userId);
    const targetEmail = targetUser?.email;
    
    if (targetEmail) {
      const chatHistory = JSON.parse(localStorage.getItem('chatHistory') || '[]');
      localStorage.setItem('chatHistory', JSON.stringify(
        chatHistory.filter(c => c.orderData?.email !== targetEmail)
      ));
      const adminOrders = JSON.parse(localStorage.getItem('admin_orders') || '[]');
      localStorage.setItem('admin_orders', JSON.stringify(
        adminOrders.filter(o => o.email !== targetEmail && o.trader_name !== targetEmail)
      ));
    }
    
    fetchDashboardData(true);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      active: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      paid: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
      disputed: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const filteredOrders = orders.filter(order => {
    if (filter !== 'all' && order.status !== filter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        order.id?.toLowerCase().includes(query) ||
        order.advertiser_name?.toLowerCase().includes(query) ||
        order.trader_name?.toLowerCase().includes(query) ||
        order.server?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const filteredUsers = users.filter(user => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (loading || loadingData) {
    return <CoinLoader fullPage label="Chargement du tableau de bord admin..." />;
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-2 py-2.5 sm:py-3">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/" className="p-1.5 sm:p-2 -ml-1.5 hover:bg-gray-800 rounded-full flex-shrink-0" aria-label="Retour">
                <ArrowLeft size={20} />
              </Link>
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield size={18} className="text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-base sm:text-xl font-bold leading-tight">Admin Dashboard</h1>
                  <p className="text-[11px] sm:text-xs text-gray-400 leading-tight">Panel d'administration AVKAMAS</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              {usingLocalStorage && (
                <span className="hidden sm:inline px-2 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs">
                  Mode LocalStorage
                </span>
              )}
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 text-xs sm:text-sm leading-tight text-right">
                <span className="text-gray-400">Admin:</span>
                <span className="font-medium truncate max-w-[6rem] sm:max-w-none">{user?.name}</span>
              </div>
              <button
                onClick={handleLogout}
                aria-label="Déconnexion"
                className="flex items-center gap-2 p-1.5 text-gray-400 hover:text-red-400 transition-colors"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mode LocalStorage badge on mobile (hidden in the header there) */}
      {usingLocalStorage && (
        <div className="sm:hidden bg-orange-500/10 text-orange-400 text-xs text-center py-1">Mode LocalStorage</div>
      )}

      {/* Statistics Cards */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 [&>div]:p-3 sm:[&>div]:p-4 [&_p.text-2xl]:text-xl sm:[&_p.text-2xl]:text-2xl">
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <ShoppingBag className="text-blue-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statistics.total_orders || 0}</p>
                  <p className="text-xs text-gray-400">Total Commandes</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <Users className="text-green-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statistics.total_users || 0}</p>
                  <p className="text-xs text-gray-400">Utilisateurs</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="text-yellow-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statistics.pending_orders || 0}</p>
                  <p className="text-xs text-gray-400">En attente</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <MessageSquare className="text-purple-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statistics.total_conversations || 0}</p>
                  <p className="text-xs text-gray-400">Conversations</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 sm:gap-4 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&>button]:px-2 sm:[&>button]:px-4 [&>button]:text-xs sm:[&>button]:text-sm [&_svg]:mr-1 sm:[&_svg]:mr-2">
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'orders' 
                  ? 'border-yellow-500 text-yellow-400' 
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <ShoppingBag size={16} className="inline mr-2" />
              Commandes
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'users' 
                  ? 'border-yellow-500 text-yellow-400' 
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <Users size={16} className="inline mr-2" />
              Utilisateurs
            </button>
            <button
              onClick={() => setActiveTab('conversations')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'conversations' 
                  ? 'border-yellow-500 text-yellow-400' 
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <MessageSquare size={16} className="inline mr-2" />
              Conversations
            </button>
            <button
              onClick={() => setActiveTab('articles')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'articles'
                  ? 'border-yellow-500 text-yellow-400'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <FileText size={16} className="inline mr-2" />
              Articles
            </button>
            <button
              onClick={() => setActiveTab('servers')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'servers'
                  ? 'border-yellow-500 text-yellow-400'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <Server size={16} className="inline mr-2" />
              Serveurs
            </button>
          </div>
        </div>
      </div>

      {/* Filters and Search (the Articles and Servers tabs have their own toolbars) */}
      <div className={`bg-gray-900 border-b border-gray-800 ${['articles', 'servers'].includes(activeTab) ? 'hidden' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-4">
            {activeTab === 'orders' && (
              <div className="flex gap-2 overflow-x-auto whitespace-nowrap -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap [scrollbar-width:none] [&>button]:flex-shrink-0 [&>button]:px-3 [&>button]:py-1.5 sm:[&>button]:px-4 sm:[&>button]:py-2 [&>button]:text-xs sm:[&>button]:text-sm">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'all' ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  Tous
                </button>
                <button
                  onClick={() => setFilter('pending')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'pending' ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  En attente ({statistics.pending_orders || 0})
                </button>
                <button
                  onClick={() => setFilter('active')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'active' ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  Actifs ({statistics.active_orders || 0})
                </button>
                <button
                  onClick={() => setFilter('completed')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'completed' ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  Complétés ({statistics.completed_orders || 0})
                </button>
                {usingLocalStorage && (
                  <button
                    onClick={() => fetchDashboardData()}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                  >
                    <RefreshCw size={16} className="inline mr-1" />
                    Actualiser
                  </button>
                )}
              </div>
            )}
            
            <div className="flex-1 w-full lg:w-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={activeTab === 'orders' ? "Rechercher par ID, client, serveur..." : "Rechercher par nom, email..."}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-sm sm:text-base text-white placeholder-gray-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {activeTab === 'articles' && <AdminArticles token={user?.token} />}
        {activeTab === 'servers' && <AdminServers token={user?.token} />}

        {activeTab === 'orders' && (
          <div className="grid xl:grid-cols-2 gap-3 sm:gap-4 items-start">
            {filteredOrders.length === 0 ? (
              <div className="text-center py-10 sm:py-16 xl:col-span-2">
                <ShoppingBag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-400 mb-2">Aucune commande</h3>
                <p className="text-gray-500">
                  {filter === 'all' && !searchQuery 
                    ? 'Aucune commande pour le moment.' 
                    : 'Aucune commande ne correspond à votre recherche.'}
                </p>
                {usingLocalStorage && (
                  <button
                    onClick={() => fetchDashboardData()}
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    <RefreshCw size={16} className="inline mr-2" />
                    Actualiser
                  </button>
                )}
              </div>
            ) : (
              filteredOrders.map((order) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-900 border border-gray-800 rounded-xl p-3 sm:p-5 hover:border-gray-700 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 rounded-lg flex items-center justify-center ${
                        order.type === 'buy' ? 'bg-yellow-500/20' : 'bg-green-500/20'
                      }`}>
                        {order.type === 'buy' ? <TrendingUp size={18} className="text-yellow-400" /> : <TrendingDown size={18} className="text-green-400" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                          <h3 className="font-semibold text-sm sm:text-base">Commande #{order.id?.slice(-8) || 'N/A'}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] sm:text-xs font-medium border ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-400">
                          {order.type === 'buy' ? 'Achat' : 'Vente'} • {order.server} • {order.amount} Kamas
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-sm sm:text-base text-yellow-400 whitespace-nowrap">{parseFloat(order.total_price || order.totalPrice || 0).toFixed(2)} DH</p>
                      <p className="text-[11px] sm:text-xs text-gray-500">{formatDate(order.created_at)}</p>
                    </div>
                  </div>

                  <div className="border-t border-gray-800 pt-3">
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 sm:gap-4 text-xs sm:text-sm">
                      <div className="min-w-0">
                        <span className="text-gray-500 text-xs">Client Email:</span>
                        <p className="text-white font-medium break-all">{order.email || order.trader_name || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-xs">Personnage:</span>
                        <p className="text-white font-medium">{order.character || order.character_name || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-xs">Téléphone:</span>
                        <p className="text-white font-medium">{order.phone || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-xs">Méthode de paiement:</span>
                        <p className="text-white font-medium">{order.payment_method || 'N/A'}</p>
                      </div>
                    </div>
                    {order.terms && order.terms !== 'Standard purchase' && (
                      <div className="mt-3 pt-3 border-t border-gray-800">
                        <span className="text-gray-500 text-xs">Détails:</span>
                        <p className="text-gray-300 text-xs mt-1">{order.terms}</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex gap-2 flex-wrap [&>button]:text-xs sm:[&>button]:text-sm">
                      {order.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleOrderStatusUpdate(order.id, 'active')}
                            className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-sm hover:bg-green-500/30 transition-colors"
                          >
                            <CheckCircle size={14} className="inline mr-1" />
                            Approuver
                          </button>
                          <button
                            onClick={() => handleOrderStatusUpdate(order.id, 'cancelled')}
                            className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30 transition-colors"
                          >
                            <XCircle size={14} className="inline mr-1" />
                            Refuser
                          </button>
                        </>
                      )}
                      {order.status === 'active' && (
                        <button
                          onClick={() => handleOrderStatusUpdate(order.id, 'completed')}
                          className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30 transition-colors"
                        >
                          <CheckCircle size={14} className="inline mr-1" />
                          Marquer complété
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => navigate(`/admin/chat/${order.id}`)}
                      className="flex items-center gap-2 text-sm text-gray-400 hover:text-yellow-400 transition-colors"
                    >
                      <MessageSquare size={16} />
                      Discuter
                    </button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-4">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-400 mb-2">Aucun utilisateur</h3>
                <p className="text-gray-500">Aucun utilisateur ne correspond à votre recherche.</p>
              </div>
            ) : (
              filteredUsers.map((userItem) => (
                <motion.div
                  key={userItem.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-900 border border-gray-800 rounded-xl p-3 sm:p-5 hover:border-gray-700 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">{userItem.name?.substring(0, 2).toUpperCase()}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{userItem.name}</h3>
                          {userItem.role === 'admin' && (
                            <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
                              Admin
                            </span>
                          )}
                          {userItem.is_demo && (
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs font-medium">
                              Demo
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400">{userItem.email}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Inscrit le</p>
                      <p className="text-sm">{formatDate(userItem.created_at)}</p>
                    </div>
                  </div>

                  {userItem.phone && (
                    <div className="mt-4 pt-4 border-t border-gray-800">
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <Phone size={16} />
                        <span>{userItem.phone}</span>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => handleDeleteUser(userItem.id)}
                      className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30 transition-colors"
                    >
                      <XCircle size={14} className="inline mr-1" />
                      Supprimer
                    </button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}

        {activeTab === 'conversations' && (
          <div className="space-y-4">
            {conversations.length === 0 ? (
              <div className="text-center py-16">
                <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-400 mb-2">Aucune conversation</h3>
                <p className="text-gray-500">Aucune conversation active pour le moment.</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <motion.div
                  key={conv.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-900 border border-gray-800 rounded-xl p-3 sm:p-5 hover:border-gray-700 transition-colors cursor-pointer"
                  onClick={() => navigate(`/admin/chat/${conv.order_id || conv.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                        <MessageSquare size={20} className="text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{conv.title || `Conversation #${conv.id}`}</h3>
                        <p className="text-sm text-gray-400">
                          Type: {conv.type} • {conv.user_name}
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Dernière activité</p>
                      <p className="text-sm">{formatDate(conv.updated_at)}</p>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
